import { withSkeletonLoading, WithSkeletonLoadingProps } from "@landtechnologies/components";
import classNames from "classnames";
import { useEffect, useMemo } from "react";
import { useTitlesByLocationQuery } from "react-migration/domains/ownership";
import { MultiTitleOwnershipInformation } from "./components/MultiTitleOwnershipInformation";
import { SingleTitleOwnershipInformation } from "./components/SingleTitleOwnershipInformation";

type Geometry = GeoJSON.MultiPolygon | GeoJSON.Polygon | GeoJSON.Point;

type OwnershipInformationProps = {
  geometry?: Geometry;
};

export const OwnershipInformation = withSkeletonLoading(
  ({
    hideSkeletonLoading,
    setSkeletonLoading,
    geometry,
  }: WithSkeletonLoadingProps & OwnershipInformationProps) => {
    const { titlesByLocation, loading: titlesByLocationLoading } = useTitlesByLocationQuery(
      geometry &&
        ({
          type: geometry.type,
          coordinates: geometry.coordinates,
        } as Geometry)
    );

    const mainTitleCount = useMemo(() => {
      return titlesByLocation.filter((title) => title.is_main_plot).length;
    }, [titlesByLocation]);

    const isMultiMainTitle = mainTitleCount > 1;
    const isSingleMainTitle = mainTitleCount === 1;

    useEffect(() => {
      setSkeletonLoading(!!geometry && !isMultiMainTitle);
    }, [geometry, isMultiMainTitle, setSkeletonLoading]);

    useEffect(() => {
      if (geometry && !titlesByLocationLoading) {
        hideSkeletonLoading();
      }
    }, [geometry, titlesByLocationLoading, hideSkeletonLoading]);

    return (
      <div
        className={classNames(
          "atlas-flex",
          "atlas-flex-col",
          "atlas-p-4",
          "atlas-bg-background-white",
          "atlas-border-b-[12px]",
          "atlas-border-neutral-200"
        )}
        data-testid="ownership-overview"
      >
        {isMultiMainTitle && (
          <MultiTitleOwnershipInformation
            titlesByLocation={titlesByLocation}
            titlesByLocationLoading={titlesByLocationLoading}
          />
        )}

        {isSingleMainTitle && (
          <SingleTitleOwnershipInformation titlesByLocation={titlesByLocation} />
        )}
      </div>
    );
  }
);
